import { Suspense, useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'

import { ArrowUpRight } from 'lucide-react'

import { Logomark } from '@/components/Logo'
import { Navigation } from '@/components/Navigation'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { useNavigationItems } from '@/hooks/useNavigationItems'
import clsx from 'clsx'

function MenuIcon(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      {...props}
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  )
}

function CloseIcon(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      {...props}
    >
      <path d="M5 5l14 14M19 5l-14 14" />
    </svg>
  )
}

function CloseOnNavigation({ close }: { close: () => void }) {
  let { pathname } = useLocation()

  useEffect(() => {
    close()
  }, [pathname, close])

  return null
}

export function MobileNavigation() {
  let [isOpen, setIsOpen] = useState(false)
  let close = useCallback(() => setIsOpen(false), [setIsOpen])
  const navigationItems = useNavigationItems()
  const { pathname } = useLocation()

  // Determine the section label based on current path
  const getSectionLabel = () => {
    if (pathname.startsWith('/troubleshoot')) return 'Troubleshooting Guides'
    if (pathname.startsWith('/sdk')) return 'SDK Reference'
    return 'Get Started'
  }

  function onLinkClick(event: React.MouseEvent<HTMLAnchorElement>) {
    let link = event.currentTarget
    if (
      link.pathname + link.search + link.hash ===
      window.location.pathname + window.location.search + window.location.hash
    ) {
      close()
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="relative"
        aria-label="Open navigation"
      >
        <MenuIcon className="h-6 w-6 stroke-slate-500" />
      </button>
      <Suspense fallback={null}>
        <CloseOnNavigation close={close} />
      </Suspense>
      <Dialog
        open={isOpen}
        onClose={() => close()}
        className="fixed inset-0 z-50 flex items-start overflow-y-auto bg-slate-900/50 pr-10 backdrop-blur lg:hidden"
        aria-label="Navigation"
      >
        <DialogPanel className="min-h-full w-full max-w-xs bg-white px-4 pb-12 pt-5 sm:px-6 dark:bg-slate-900">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => close()}
              aria-label="Close navigation"
            >
              <CloseIcon className="h-6 w-6 stroke-slate-500" />
            </button>
            <Link to="/" className="ml-6" aria-label="Home page">
              <Logomark className="h-9 w-9" />
            </Link>
          </div>
          <div className="mt-5 flex flex-col gap-4 px-1">
            {/* Top-level navigation links */}
            <nav className="flex flex-col gap-2 border-b border-slate-200 pb-4 dark:border-slate-800">
              <Link
                to="/"
                onClick={() => close()}
                className={clsx(
                  'rounded-md px-4 py-2 text-sm font-semibold transition-colors',
                  pathname === '/' || pathname.startsWith('/docs')
                    ? 'bg-sky-500 text-white'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                )}
              >
                Get Started
              </Link>
              <Link
                to="/troubleshoot"
                onClick={() => close()}
                className={clsx(
                  'rounded-md px-4 py-2 text-sm font-semibold transition-colors',
                  pathname.startsWith('/troubleshoot')
                    ? 'bg-sky-500 text-white'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                )}
              >
                Troubleshooting Guides
              </Link>
              <Link
                to="/release-notes"
                onClick={() => close()}
                className={clsx(
                  'rounded-md px-4 py-2 text-sm font-semibold transition-colors',
                  pathname === '/release-notes'
                    ? 'bg-sky-500 text-white'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                )}
              >
                Release Notes
              </Link>
            </nav>

            {/* Current section's detailed navigation */}
            <Disclosure defaultOpen>
              {({ open }) => (
                <>
                  <DisclosureButton className="flex w-full justify-between px-4 py-2 text-left text-sm font-medium text-slate-700 dark:text-slate-300">
                    <span>{getSectionLabel()}</span>
                    <ChevronUpDownIcon
                      className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-slate-500`}
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="px-4 pb-2 pt-4 text-sm text-slate-500">
                    <Navigation onLinkClick={onLinkClick} navigationItems={navigationItems} />
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>
            <a
              href="https://noctuagames.com/publishing"
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-300"
              target="_blank"
              rel="noopener noreferrer"
            >
              Publishing
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </DialogPanel>
      </Dialog>
    </>
  )
}
